import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bot, Handshake, Loader2 } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { id: "bank_transfer", label: "Bank transfer" }, { id: "direct_debit", label: "Direct debit" },
  { id: "card", label: "Card" }, { id: "cash", label: "Cash" }, { id: "other", label: "Other" },
];
const money = (v, cur = "£") => `${cur}${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function prefillFromTriage(t) {
  const amounts = (t?.amounts || []).filter(a => a.amount);
  const isPromise = t?.category === "Promise to pay" || t?.category === "Arrangement request" || !!t?.promised_date;
  const promised = amounts.find(a => a.what === "promised") || (isPromise ? amounts[0] : null);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(t?.promised_date || "") ? t.promised_date : "";
  return { amount: promised?.amount ? String(promised.amount) : "", date: d, method: "bank_transfer", notes: t?.note_summary || t?.summary || "" };
}

/** Dean (9 Oct): one click turns a "Promise to pay" email into a P2P on the file. The dialog opens pre-filled from the AI
 *  (amount, date, method), the collector checks or edits it, and on Add the reply box opens with the confirmation pre-drafted. */
export function PromiseCaptureDialog({ email, api, me, matched, onClose, onAdded }) {
  const t = email.triage;
  const [form, setForm] = useState(() => prefillFromTriage(t));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const aiFilled = !!(t?.promised_date || (t?.amounts || []).some(a => a.what === "promised"));

  useEffect(() => {
    if (t) return;
    api.p2pPrefill(email.id).then(r => {
      const p = r.prefill || {};
      setForm(f => ({ ...f, amount: p.promised_amount ? String(p.promised_amount) : f.amount, date: p.promised_at || f.date, notes: p.notes || f.notes }));
    }).catch(() => {});
  }, [email.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const amountNum = Number(String(form.amount).replace(/[£A$,\s]/g, ""));
  const valid = amountNum > 0 && /^\d{4}-\d{2}-\d{2}$/.test(form.date);

  const submit = async () => {
    if (!valid) { toast.error("Enter the promised amount and the date"); return; }
    setBusy(true);
    try {
      const r = await api.capturePromise(email.id, {
        case_id: matched.case_id, promised_at: form.date, promised_amount: amountNum, method: form.method, notes: form.notes,
      });
      toast.success(`Promise to pay added to ${r.case?.sf || r.case?.customer_name} — ${money(r.p2p.promised_amount, api.currency)} on ${r.p2p.promised_at}`);
      onAdded(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md overflow-hidden" aria-describedby={undefined} data-testid="p2p-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Handshake className="w-4 h-4 text-amber-600" />Add promise to pay</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50 min-w-0">
            <div className="truncate"><b>File:</b> <span className="font-mono">{matched.sf}</span> {matched.customer_name}</div>
            <div className="truncate"><b>Email:</b> {email.from_name || email.from_email} — {email.subject || "(no subject)"}</div>
          </div>
          {aiFilled && (
            <div className="text-[10px] text-violet-700 flex items-center gap-1" data-testid="p2p-ai-hint">
              <Bot className="w-3 h-3" />Pre-filled from the AI triage — check the figures against the email before adding.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Amount promised ({api.currency})</span>
              <Input value={form.amount} onChange={(e) => set("amount", e.target.value)} inputMode="decimal" placeholder="0.00" className="h-8 text-sm" data-testid="p2p-amount" />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Promised by</span>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-8 text-sm" data-testid="p2p-date" />
            </label>
          </div>
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Method</span>
            <select value={form.method} onChange={(e) => set("method", e.target.value)} className="h-8 w-full border rounded px-2 text-sm bg-white" data-testid="p2p-method">
              {METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Note on the file (optional)</span>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="text-xs" data-testid="p2p-notes" />
          </label>
          <p className="text-[10px] text-muted-foreground">
            Adds the promise to the file's P2P calendar, logs this email on the timeline if it isn't there yet, then opens a reply pre-drafted to confirm it to the customer. Nothing is sent until you press Send.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !valid} className="bg-amber-600 hover:bg-amber-700" data-testid="p2p-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Handshake className="w-4 h-4 mr-1" />}
            {valid ? `Add ${money(amountNum, api.currency)} on ${form.date} & draft reply` : "Add promise & draft reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
